import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getUserCycleById } from '../../services/admin/admin-user-cycles.service';
import styled from 'styled-components';
import breakpoints from '../../design-system/breakpoints';
import Variables from '../../design-system/variables';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvTable from '../UI/molecules/OvTable';
import { UserCycleDataUtils } from '../../common/utils/services/user-cycle-data-utils';
import DateUtils from '../../common/utils/services/date-utils';
import { Constants } from '../../common/constants';

const UserCycleDetails = () => {
  const { t } = useTranslation();
  const { userCycleId }: any = useParams();

  const {
    data: userCycle,
    isPending: isGetUserCyclePending,
    isFetching: isGetUserCycleFetching,
    error: getUserCycleError,
  } = useQuery({
    queryKey: ['user-cycle', userCycleId],
    queryFn: () => getUserCycleById(userCycleId),
    retry: 3,
    enabled: !!userCycleId,
  });

  const isLoading = isGetUserCycleFetching || isGetUserCyclePending;

  return (
    <DetailPageWrapper>
      <DetailsHeader>
        <DetailsWrapper>
          <OvBackButton label={t('common.actions.back')} />
          {`Cycle: ${DateUtils.formatDate(
            userCycle?.start_day as unknown as Date,
            Constants.US_DATE_DISPLAY_FORMAT_WITHOUT_TIME
          )} - ${DateUtils.formatDate(
            userCycle?.end_day as unknown as Date,
            Constants.US_DATE_DISPLAY_FORMAT_WITHOUT_TIME
          )} (${userCycle?.cycle_length} days)`}
        </DetailsWrapper>
        {getUserCycleError && (
          <span>
            <OvErrorMessage message={getUserCycleError?.message} />
          </span>
        )}
      </DetailsHeader>

      <DetailPageContentWrapper>
        <TableWrapper>
          <OvTable
            data={UserCycleDataUtils.mapUserCycleToTableData(userCycle)}
          />
        </TableWrapper>
      </DetailPageContentWrapper>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </DetailPageWrapper>
  );
};

export default UserCycleDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
  border-radius: ${Variables.borderRadius.SMALL};
`;

const DetailsHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  font-weight: bold;
`;

const DetailsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DetailPageContentWrapper = styled.div`
  display: flex;
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  align-self: flex-start;
  margin-left: 12px;
  width: 100%;

  @media (max-width: ${breakpoints.md}) {
    margin-left: 0;
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;
